/**
 * PyMOL Command Parser (Lite)
 * 
 * Translates PyMOL-style commands into messaging protocol commands
 * for the external Molstar viewer.
 * 
 * Based on the MolstarPyMol.tsx clone but adapted to work with
 * the cross-domain messaging protocol instead of direct Molstar API calls.
 */

export interface ViewerMessage {
  description: string
  target?: { name?: string; uuid?: string }
  result?: any
}

export interface ParsedPyMolCommand {
  messages: ViewerMessage[]
  assistantResponse?: string
  error?: string
}

/**
 * Parse a single PyMOL command into viewer messages
 */
export function parsePyMolCommand(input: string, currentAssetName: string = 'PDBAsset'): ParsedPyMolCommand {
  const trimmed = input.trim()
  if (!trimmed) {
    return { messages: [], error: 'Empty command' }
  }

  const [head, ...rest] = trimmed.split(' ')
  const cmd = head.toLowerCase()
  const tail = rest.join(' ').trim()

  try {
    switch (cmd) {
      case 'fetch':
        return parseFetch(tail)
      
      case 'remove':
        return parseRemove(tail, currentAssetName)
      
      case 'hide':
        return parseHide(tail, currentAssetName)
      
      case 'show':
        return parseShow(tail, currentAssetName)
      
      case 'as':
        return parseAs(tail, currentAssetName)
      
      case 'spectrum':
        return parseSpectrum(tail, currentAssetName)
      
      case 'set':
        return parseSet(tail, currentAssetName)
      
      case 'bg_color':
      case 'bgcolor':
        return parseBgColor(tail)
      
      case 'orient':
      case 'reset':
        return parseOrient(currentAssetName)
      
      case 'zoom':
        return parseZoom(tail, currentAssetName)
      
      case 'center':
        return parseCenter(tail, currentAssetName)
      
      case 'color':
        return parseColor(tail, currentAssetName)
      
      case 'select':
        return parseSelect(tail, currentAssetName)
      
      case 'delete':
        return parseDelete(tail, currentAssetName)
      
      case 'enable':
        return parseEnable(tail, currentAssetName)
      
      case 'disable':
        return parseDisable(tail, currentAssetName)
      
      case 'label':
        return parseLabel(tail, currentAssetName)
      
      case 'distance':
        return parseDistance(tail, currentAssetName)
      
      case 'angle':
        return parseAngle(tail, currentAssetName)
      
      case 'dihedral':
        return parseDihedral(tail, currentAssetName)
      
      case 'turn':
        return parseTurn(tail)
      
      case 'rock':
        return parseRock(tail)
      
      case 'help':
        return parseHelp(tail)
      
      default:
        return {
          messages: [],
          error: `Unknown PyMOL command: ${cmd}. Type "help pymol" for supported commands.`
        }
    }
  } catch (e) {
    return {
      messages: [],
      error: `Error parsing command: ${(e as Error).message}`
    }
  }
}

/**
 * Parse multiple PyMOL commands (one per line)
 */
export function parsePyMolCommands(input: string, currentAssetName: string = 'PDBAsset'): ParsedPyMolCommand {
  const lines = input.split('\n').map(l => l.trim()).filter(Boolean)
  const allMessages: ViewerMessage[] = []
  const errors: string[] = []
  const responses: string[] = []

  for (const line of lines) {
    const result = parsePyMolCommand(line, currentAssetName)
    if (result.error) {
      errors.push(`Line "${line}": ${result.error}`)
    } else {
      allMessages.push(...result.messages)
      if (result.assistantResponse) {
        responses.push(result.assistantResponse)
      }
    }
  }

  return {
    messages: allMessages,
    assistantResponse: responses.length > 0 ? responses.join('\n') : undefined,
    error: errors.length > 0 ? errors.join('\n') : undefined
  }
}

/**
 * Detect if input looks like PyMOL command(s)
 */
export function isPyMolCommand(input: string): boolean {
  const trimmed = input.trim()
  const pymolKeywords = [
    'fetch', 'remove', 'hide', 'show', 'as', 'spectrum', 'set', 
    'bg_color', 'bgcolor', 'orient', 'reset', 'color', 'select',
    'zoom', 'center', 'delete', 'enable', 'disable', 'label',
    'distance', 'angle', 'dihedral', 'turn', 'rock'
  ]
  
  // Check if any line starts with a PyMOL keyword
  const lines = trimmed.split('\n')
  return lines.some(line => {
    const firstWord = line.trim().split(' ')[0]?.toLowerCase()
    return pymolKeywords.includes(firstWord)
  })
}

// ============================================================================
// Command Parsers
// ============================================================================

// ============================================================================
// Color and Style Mappings
// ============================================================================

const COLOR_SCHEME_MAP: Record<string, string> = {
  'chain': 'chain',
  'element': 'element',
  'residue': 'residue',
  'spectrum': 'spectrum',
  'hydrophobicity': 'hydrophobicity',
  'volume': 'volume',
  'charge': 'partialCharge',
  'partialcharge': 'partialCharge',
  'formalcharge': 'formalCharge',
  'rainbow': 'spectrum',
}

const STYLE_MAP: Record<string, string> = {
  'cartoon': 'cartoon',
  'ribbon': 'cartoon',
  'sticks': 'ball-and-stick',
  'stick': 'ball-and-stick',
  'ball-and-stick': 'ball-and-stick',
  'spheres': 'spacefill',
  'sphere': 'spacefill',
  'spacefill': 'spacefill',
  'lines': 'backbone',
  'line': 'backbone',
  'backbone': 'backbone',
  'putty': 'putty',
  'carbohydrate': 'carbohydrate',
}

const COLOR_MAP: Record<string, [number, number, number]> = {
  'red': [1, 0, 0],
  'green': [0, 1, 0],
  'blue': [0, 0, 1],
  'yellow': [1, 1, 0],
  'cyan': [0, 1, 1],
  'magenta': [1, 0, 1],
  'white': [1, 1, 1],
  'black': [0, 0, 0],
  'gray': [0.5, 0.5, 0.5],
  'grey': [0.5, 0.5, 0.5],
  'orange': [1, 0.5, 0],
  'purple': [0.5, 0, 0.5],
  'violet': [0.56, 0, 1],
  'pink': [1, 0.75, 0.8],
  'brown': [0.65, 0.16, 0.16],
  'lime': [0.75, 1, 0],
  'navy': [0, 0, 0.5],
  'teal': [0, 0.5, 0.5],
  'maroon': [0.5, 0, 0],
  'olive': [0.5, 0.5, 0],
  'salmon': [1, 0.55, 0.41],
  'gold': [1, 0.84, 0],
}

const BG_COLOR_MAP: Record<string, string> = {
  'black': '#000000',
  'white': '#ffffff',
  'gray': '#808080',
  'grey': '#808080',
  'red': '#ff0000',
  'green': '#00ff00',
  'blue': '#0000ff',
  'yellow': '#ffff00',
  'cyan': '#00ffff',
  'magenta': '#ff00ff',
  'orange': '#ff8000',
  'purple': '#800080'
}

/**
 * fetch 1ubq, async=0
 * fetch cid:2244 (PubChem)
 * fetch pubchem:aspirin
 * Translates to AddAssets with PDB or PubChem file
 */
function parseFetch(tail: string): ParsedPyMolCommand {
  const [idAndMaybeComma, ...argRest] = tail.split(' ')
  let id = idAndMaybeComma.replace(',', '').trim()
  
  // Check for PubChem prefixes
  if (id.toLowerCase().startsWith('cid:')) {
    // CID number
    const cid = id.substring(4).trim()
    if (!cid || !/^\d+$/.test(cid)) {
      return { messages: [], error: 'fetch cid: requires a numeric CID (e.g., "fetch cid:2244")' }
    }
    
    return {
      messages: [{
        description: 'AddAssets',
        result: [{
          name: `CID_${cid}`,
          type: 'molecular-data',
          data: {
            url: `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/${cid}/record/SDF/?record_type=3d`,
            type: 'sdf',
            isBinary: false,
            colorScheme: 'element',
            style: 'ball-and-stick',
            thickness: 1
          },
          scene: 'ROOT',
          showOnLoad: true
        }]
      }],
      assistantResponse: `Loading PubChem CID ${cid}`
    }
  } else if (id.toLowerCase().startsWith('pubchem:')) {
    // PubChem name lookup
    const name = id.substring(8).trim()
    if (!name) {
      return { messages: [], error: 'fetch pubchem: requires a compound name (e.g., "fetch pubchem:aspirin")' }
    }
    
    return {
      messages: [{
        description: 'AddAssets',
        result: [{
          name: `PUBCHEM_${name.toUpperCase()}`,
          type: 'molecular-data',
          data: {
            url: `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(name)}/record/SDF/?record_type=3d`,
            type: 'sdf',
            isBinary: false,
            colorScheme: 'element',
            style: 'ball-and-stick',
            thickness: 1
          },
          scene: 'ROOT',
          showOnLoad: true
        }]
      }],
      assistantResponse: `Loading ${name} from PubChem`
    }
  }
  
  // Default: PDB ID (must be 4 characters)
  const pdbId = id.toLowerCase()
  if (!pdbId || pdbId.length !== 4) {
    return { 
      messages: [], 
      error: 'fetch requires a 4-character PDB ID or use "cid:###" for PubChem CID or "pubchem:name" for compound name' 
    }
  }

  const url = `https://files.rcsb.org/download/${pdbId}.cif`
  
  return {
    messages: [{
      description: 'AddAssets',
      result: [{
        name: `PDB_${pdbId.toUpperCase()}`,
        type: 'molecular-data',
        data: {
          url,
          type: 'cif',
          isBinary: false,
          colorScheme: 'chain',
          style: 'cartoon',
          thickness: 1
        },
        scene: 'ROOT',
        showOnLoad: true
      }]
    }],
    assistantResponse: `Loading PDB structure ${pdbId.toUpperCase()} from RCSB`
  }
}

/**
 * remove solvent | water | <selection>
 */
function parseRemove(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase()
  
  if (arg.startsWith('solvent') || arg.startsWith('water')) {
    return {
      messages: [],
      assistantResponse: 'Note: Solvent removal is not currently supported. The structure loads without solvent by default for most PDB files.',
      error: 'remove solvent not supported'
    }
  }
  
  return {
    messages: [],
    error: `remove command not fully supported. Try using "delete" to remove entire molecules.`
  }
}

/**
 * hide everything | surface | ligands | <representation>
 */
function parseHide(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase()
  
  if (arg.startsWith('everything') || arg.startsWith('all') || !arg) {
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { visible: false }
      }],
      assistantResponse: 'Hiding molecular structure'
    }
  } else if (arg.startsWith('surface')) {
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { showSurface: false }
      }],
      assistantResponse: 'Hiding molecular surface'
    }
  } else if (arg.startsWith('ligand')) {
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { showLigand: false }
      }],
      assistantResponse: 'Hiding ligands'
    }
  }
  
  return {
    messages: [],
    error: `hide command not recognized. Try "hide everything", "hide surface", or "hide ligands"`
  }
}

/**
 * show cartoon | surface | sticks | spheres | backbone | putty | ribbon | mesh
 */
function parseShow(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  const messages: ViewerMessage[] = []
  
  // Make sure it's visible
  messages.push({
    description: 'SetComponentProperties',
    target: { name: assetName },
    result: { visible: true }
  })
  
  if (arg.startsWith('cartoon') || arg.startsWith('ribbon')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'cartoon', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing cartoon representation'
    }
  } else if (arg.startsWith('surface')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { showSurface: true, surfaceAlpha: 1.0 }
    })
    return {
      messages,
      assistantResponse: 'Showing molecular surface'
    }
  } else if (arg.startsWith('mesh')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { showSurface: true, surfaceAlpha: 0.5 }
    })
    return {
      messages,
      assistantResponse: 'Showing mesh surface (semi-transparent)'
    }
  } else if (arg.startsWith('sticks') || arg.startsWith('stick')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'ball-and-stick', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing ball-and-stick representation'
    }
  } else if (arg.startsWith('spheres') || arg.startsWith('sphere')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'spacefill', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing spacefill representation'
    }
  } else if (arg.startsWith('lines') || arg.startsWith('line') || arg.startsWith('backbone')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'backbone', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing backbone representation'
    }
  } else if (arg.startsWith('putty')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'putty', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing putty representation (proteins only)'
    }
  } else if (arg.startsWith('carbohydrate')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'carbohydrate', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing carbohydrate representation'
    }
  } else if (arg.startsWith('ball-and-stick')) {
    messages.push({
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style: 'ball-and-stick', showSurface: false }
    })
    return {
      messages,
      assistantResponse: 'Showing ball-and-stick representation'
    }
  }
  
  return {
    messages: [],
    error: `show command not recognized. Supported: cartoon, surface, mesh, sticks, spheres, backbone, putty, carbohydrate`
  }
}

/**
 * as cartoon | sticks | spheres | ribbon | lines | backbone | putty
 * Shorthand for changing representation
 */
function parseAs(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  const style = STYLE_MAP[arg]
  if (!style) {
    return {
      messages: [],
      error: `Unknown representation: ${arg}. Try: cartoon, sticks, spheres, ribbon, lines, backbone, putty, carbohydrate`
    }
  }
  
  return {
    messages: [{
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { style, showSurface: false, visible: true }
    }],
    assistantResponse: `Changing representation to ${arg}`
  }
}

/**
 * spectrum element | residue | chain | hydrophobicity | charge | rainbow
 */
function parseSpectrum(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  const scheme = COLOR_SCHEME_MAP[arg]
  if (!scheme) {
    return {
      messages: [],
      error: `Unknown spectrum: ${arg}. Try: element, residue, chain, hydrophobicity, charge, rainbow`
    }
  }
  
  return {
    messages: [{
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { colorScheme: scheme }
    }],
    assistantResponse: `Applying ${arg} color spectrum`
  }
}

/**
 * set transparency, 0.3
 * set stick_radius, 0.5
 * set surface_transparency, 0.5
 * set surface_color, red
 * set shininess, 50
 * set bg_rgb, [1, 1, 1]
 */
function parseSet(tail: string, assetName: string): ParsedPyMolCommand {
  const [key, ...valueParts] = tail.split(',').map(s => s.trim())
  const value = valueParts.join(',').trim()
  
  if (!key || !value) {
    return { messages: [], error: 'set requires property and value' }
  }
  
  const keyLower = key.toLowerCase()
  
  // Transparency
  if (keyLower === 'transparency' || keyLower === 'surface_transparency') {
    const alpha = 1.0 - Number(value)
    if (Number.isNaN(alpha)) {
      return { messages: [], error: 'transparency value must be a number' }
    }
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { surfaceAlpha: Math.max(0, Math.min(1, alpha)) }
      }],
      assistantResponse: `Setting transparency to ${value}`
    }
  }
  
  // Thickness properties
  if (keyLower === 'stick_radius' || keyLower === 'sphere_scale' || 
      keyLower === 'cartoon_thickness' || keyLower === 'ribbon_width' ||
      keyLower === 'cartoon_sampling') {
    const thickness = Number(value)
    if (Number.isNaN(thickness)) {
      return { messages: [], error: 'thickness value must be a number' }
    }
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { thickness: Math.max(0.1, thickness) }
      }],
      assistantResponse: `Setting ${key} to ${value}`
    }
  }
  
  // Surface control
  if (keyLower === 'surface') {
    const val = value.toLowerCase()
    const showSurface = (val === '1' || val === 'on' || val === 'true')
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { showSurface }
      }],
      assistantResponse: showSurface ? 'Enabling molecular surface' : 'Disabling molecular surface'
    }
  }
  
  // Surface mode
  if (keyLower === 'surface_mode') {
    const mode = parseInt(value)
    let surfaceAlpha = 1.0
    if (mode === 1) surfaceAlpha = 0.5
    else if (mode === 2) surfaceAlpha = 0.3
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { surfaceAlpha }
      }],
      assistantResponse: `Setting surface mode to ${mode}`
    }
  }
  
  // Surface color / Cartoon color
  if (keyLower === 'surface_color' || keyLower === 'cartoon_color') {
    const colorName = value.toLowerCase()
    const propPrefix = keyLower === 'surface_color' ? 'surface' : ''
    
    // First check if it's a color scheme
    const scheme = COLOR_SCHEME_MAP[colorName]
    if (scheme) {
      const result: any = keyLower === 'surface_color' 
        ? { surfaceColorScheme: scheme }
        : { colorScheme: scheme }
      return {
        messages: [{
          description: 'SetComponentProperties',
          target: { name: assetName },
          result
        }],
        assistantResponse: `Setting ${key.replace('_', ' ')} scheme to ${colorName}`
      }
    }
    
    // Otherwise try as a named color
    const rgb = COLOR_MAP[colorName]
    if (!rgb) {
      return { messages: [], error: `Unknown color or scheme: ${colorName}` }
    }
    
    const result: any = keyLower === 'surface_color'
      ? { surfaceColorScheme: 'uniform', uniformSurfaceColor: rgb }
      : { colorScheme: 'uniform', uniformModelColor: rgb }
    
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result
      }],
      assistantResponse: `Setting ${key.replace('_', ' ')} to ${colorName}`
    }
  }
  
  // Shininess
  if (keyLower === 'shininess') {
    const shine = Number(value)
    if (Number.isNaN(shine)) {
      return { messages: [], error: 'shininess value must be a number' }
    }
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { shininess: Math.max(0, shine) }
      }],
      assistantResponse: `Setting shininess to ${value}`
    }
  }
  
  if (keyLower === 'surface_shininess') {
    const shine = Number(value)
    if (Number.isNaN(shine)) {
      return { messages: [], error: 'surface_shininess value must be a number' }
    }
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { surfaceShininess: Math.max(0, shine) }
      }],
      assistantResponse: `Setting surface shininess to ${value}`
    }
  }
  
  // Background color
  if (keyLower === 'bg_rgb' || keyLower === 'ray_trace_color') {
    // Try to parse RGB array or color name
    const colorName = value.toLowerCase()
    const hexColor = BG_COLOR_MAP[colorName] || colorName
    return {
      messages: [{
        description: 'SetBackground',
        result: { color: hexColor, colorDuration: 0.5, style: '' }  // Clear any gradient style
      }],
      assistantResponse: `Setting background color`
    }
  }
  
  // Sweep speed (orbit period)
  if (keyLower === 'sweep_speed') {
    const period = Number(value)
    if (Number.isNaN(period)) {
      return { messages: [], error: 'sweep_speed value must be a number' }
    }
    // Clamp period between 0 and 300 seconds
    const clampedPeriod = Math.max(0, Math.min(300, period))
    return {
      messages: [{
        description: 'SetAutoOrbit',
        result: clampedPeriod > 0 ? { period: clampedPeriod } : false
     }],
      assistantResponse: clampedPeriod > 0 
        ? `Setting orbit speed to ${clampedPeriod} seconds per rotation`
        : 'Stopping auto-rotation'
    }
  }
  
  // Not directly supported properties
  if (keyLower === 'cartoon_fancy_helices' || keyLower === 'stick_quality' || 
      keyLower === 'sphere_quality' || keyLower === 'ambient' || keyLower === 'specular') {
    return {
      messages: [],
      assistantResponse: `Note: ${key} setting is not directly controllable in this viewer. Default high-quality rendering is used.`
    }
  }
  
  return {
    messages: [],
    error: `set property "${key}" not recognized. See documentation for supported properties.`
  }
}

/**
 * bg_color black | white | gray | snorkle | #hexcolor
 */
function parseBgColor(tail: string): ParsedPyMolCommand {
  const colorName = tail.toLowerCase().trim()
  
  // Special case: "snorkle" gradient
  if (colorName === 'snorkle') {
    return {
      messages: [{
        description: 'SetBackground',
        result: { style: 'linear-gradient(to bottom, #000000, #0a1628, #1e1b4b)' }  // Snorkle gradient: black → dark blue → dark purple
      }],
      assistantResponse: `Setting background to Snorkle gradient`
    }
  }
  
  const color = BG_COLOR_MAP[colorName] || colorName
  
  return {
    messages: [{
      description: 'SetBackground',
      result: { color, colorDuration: 0.5, style: '' }  // Clear any gradient style
    }],
    assistantResponse: `Setting background color to ${colorName}`
  }
}

/**
 * orient | reset
 * Reset camera to view entire molecule
 */
function parseOrient(assetName: string): ParsedPyMolCommand {
  return {
    messages: [{
      description: 'SetCameraPosition',
      result: { 
        target: { name: assetName, mode: 'flyTo' },
        duration: 0.5
      }
    }],
    assistantResponse: 'Resetting camera orientation'
  }
}

/**
 * zoom [selection]
 * Focus camera on selection or entire molecule
 */
function parseZoom(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  if (!arg || arg === 'all') {
    return {
      messages: [{
        description: 'SetCameraPosition',
        result: { 
          target: { name: assetName, mode: 'flyTo' },
          duration: 0.5
        }
      }],
      assistantResponse: 'Zooming to entire molecule'
    }
  }
  
  // Parse selection
  const selection = parseSelection(arg)
  if (selection.error) {
    return { messages: [], error: selection.error }
  }
  
  return {
    messages: [{
      description: 'FocusOnTarget',
      target: { name: assetName },
      result: { ...selection.focus, cameraMode: 'flyTo', isolateRadius: 5 }
    }],
    assistantResponse: `Zooming to ${arg}`
  }
}

/**
 * center [selection]
 * Center view on selection without flying
 */
function parseCenter(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  if (!arg || arg === 'all') {
    return {
      messages: [{
        description: 'SetCameraPosition',
        result: { 
          target: { name: assetName, mode: 'lookAt' },
          duration: 0.5
        }
      }],
      assistantResponse: 'Centering view on molecule'
    }
  }
  
  // Parse selection
  const selection = parseSelection(arg)
  if (selection.error) {
    return { messages: [], error: selection.error }
  }
  
  return {
    messages: [{
      description: 'FocusOnTarget',
      target: { name: assetName },
      result: { ...selection.focus, cameraMode: 'lookAt', isolateRadius: 5 }
    }],
    assistantResponse: `Centering view on ${arg}`
  }
}

/**
 * color red | blue | by chain | by element | by hydrophobicity
 */
function parseColor(tail: string, assetName: string): ParsedPyMolCommand {
  const parts = tail.split(',').map(s => s.trim())
  const colorArg = parts[0]?.toLowerCase()
  
  if (!colorArg) {
    return { messages: [], error: 'color command requires a color name or scheme' }
  }
  
  // Check if it's a scheme keyword (by X)
  if (colorArg.startsWith('by ')) {
    const schemeName = colorArg.substring(3).trim()
    const scheme = COLOR_SCHEME_MAP[schemeName]
    if (!scheme) {
      return { messages: [], error: `Unknown color scheme: ${schemeName}` }
    }
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { colorScheme: scheme }
      }],
      assistantResponse: `Coloring by ${schemeName}`
    }
  }
  
  // Check if it's a named color
  const rgb = COLOR_MAP[colorArg]
  if (!rgb) {
    return { messages: [], error: `Unknown color: ${colorArg}` }
  }
  
  return {
    messages: [{
      description: 'SetComponentProperties',
      target: { name: assetName },
      result: { 
        colorScheme: 'uniform',
        uniformModelColor: rgb
      }
    }],
    assistantResponse: `Coloring structure ${colorArg}`
  }
}

/**
 * select chain A | resi 10 | resi 10-20 | atom 42 | ligand | none
 */
function parseSelect(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  if (arg === 'none' || !arg) {
    return {
      messages: [{
        description: 'SetSelection',
        target: { name: assetName },
        result: {}
      }],
      assistantResponse: 'Clearing selection'
    }
  }
  
  if (arg === 'all') {
    return {
      messages: [{
        description: 'SetSelection',
        target: { name: assetName },
        result: {}
      }],
      assistantResponse: 'Selecting all (clearing specific selection)'
    }
  }
  
  if (arg.startsWith('chain ')) {
    const chainId = tail.substring(6).trim().toUpperCase()
    return {
      messages: [{
        description: 'SetSelection',
        target: { name: assetName },
        result: { chain: chainId }
      }],
      assistantResponse: `Selecting chain ${chainId}`
    }
  }
  
  if (arg.startsWith('resi ') || arg.startsWith('residue ')) {
    const resiStr = tail.substring(tail.indexOf(' ') + 1).trim()
    
    // Check for range (e.g., 10-20)
    if (resiStr.includes('-')) {
      const [start, end] = resiStr.split('-').map(s => parseInt(s.trim()))
      if (Number.isNaN(start) || Number.isNaN(end)) {
        return { messages: [], error: 'residue range must be two integers' }
      }
      
      // For now, just select the first residue in range
      // Full range selection would require multiple calls or range support in viewer
      return {
        messages: [{
          description: 'SetSelection',
          target: { name: assetName },
          result: { residue: start }
        }],
        assistantResponse: `Selecting residue range ${start}-${end} (showing residue ${start})`
      }
    }
    
    const resiNum = parseInt(resiStr)
    if (Number.isNaN(resiNum)) {
      return { messages: [], error: 'residue number must be an integer' }
    }
    return {
      messages: [{
        description: 'SetSelection',
        target: { name: assetName },
        result: { residue: resiNum }
      }],
      assistantResponse: `Selecting residue ${resiNum}`
    }
  }
  
  if (arg.startsWith('atom ')) {
    const atomStr = tail.substring(5).trim()
    const atomNum = parseInt(atomStr)
    if (Number.isNaN(atomNum)) {
      return { messages: [], error: 'atom number must be an integer' }
    }
    return {
      messages: [{
        description: 'SetSelection',
        target: { name: assetName },
        result: { atom: atomNum }
      }],
      assistantResponse: `Selecting atom ${atomNum}`
    }
  }
  
  if (arg.startsWith('ligand')) {
    return {
      messages: [{
        description: 'SetComponentProperties',
        target: { name: assetName },
        result: { showLigand: true }
      }],
      assistantResponse: 'Showing ligands'
    }
  }
  
  return {
    messages: [],
    error: 'select command not recognized. Try "select chain A", "select resi 10", or "select atom 42"'
  }
}

/**
 * delete all | <name>
 */
function parseDelete(tail: string, assetName: string): ParsedPyMolCommand {
  const arg = tail.toLowerCase().trim()
  
  if (!arg || arg === 'all') {
    return {
      messages: [{
        description: 'RemoveAsset',
        target: { name: assetName }
      }],
      assistantResponse: 'Deleting molecule'
    }
  }
  
  // If specific name provided, use that
  return {
    messages: [{
      description: 'RemoveAsset',
      target: { name: tail.trim() }
    }],
    assistantResponse: `Deleting ${tail.trim()}`
  }
}

/**
 * enable <name>
 */
function parseEnable(tail: string, assetName: string): ParsedPyMolCommand {
  const targetName = tail.trim() || assetName
  
  return {
    messages: [{
      description: 'SetComponentProperties',
      target: { name: targetName },
      result: { visible: true }
    }],
    assistantResponse: `Enabling ${targetName}`
  }
}

/**
 * disable <name>
 */
function parseDisable(tail: string, assetName: string): ParsedPyMolCommand {
  const targetName = tail.trim() || assetName
  
  return {
    messages: [{
      description: 'SetComponentProperties',
      target: { name: targetName },
      result: { visible: false }
    }],
    assistantResponse: `Disabling ${targetName}`
  }
}

/**
 * label resi 10, "Active Site" | label chain A, "Chain A"
 * Note: Position calculation is approximate
 */
function parseLabel(tail: string, assetName: string): ParsedPyMolCommand {
  const parts = tail.split(',').map(s => s.trim())
  
  if (parts.length < 2) {
    return {
      messages: [],
      error: 'label requires selection and text, e.g., "label resi 10, Active Site"'
    }
  }
  
  const selection = parts[0].toLowerCase()
  const labelText = parts.slice(1).join(',').trim().replace(/["']/g, '')
  
  return {
    messages: [],
    assistantResponse: `Note: Label command is partially supported. Labels require position calculation. Try using click-to-label functionality instead.`,
    error: 'label command requires position data not available through messaging protocol'
  }
}

/**
 * distance resi 10, resi 20
 * Note: Requires position queries
 */
function parseDistance(tail: string, assetName: string): ParsedPyMolCommand {
  return {
    messages: [],
    assistantResponse: `Note: Distance measurement requires position data not available through messaging protocol. This feature is not yet supported.`,
    error: 'distance command not yet implemented'
  }
}

/**
 * angle resi 10, resi 20, resi 30
 */
function parseAngle(tail: string, assetName: string): ParsedPyMolCommand {
  return {
    messages: [],
    assistantResponse: `Note: Angle measurement requires position data not available through messaging protocol. This feature is not yet supported.`,
    error: 'angle command not yet implemented'
  }
}

/**
 * dihedral resi 10, resi 20, resi 30, resi 40
 */
function parseDihedral(tail: string, assetName: string): ParsedPyMolCommand {
  return {
    messages: [],
    assistantResponse: `Note: Dihedral measurement requires position data. This feature is not yet supported.`,
    error: 'dihedral command not yet implemented'
  }
}

/**
 * turn x, 90 | turn y, -45
 * Note: Requires camera position calculation
 */
function parseTurn(tail: string): ParsedPyMolCommand {
  return {
    messages: [],
    assistantResponse: `Note: Turn command requires camera position calculation. Use mouse to rotate the view instead.`,
    error: 'turn command not yet implemented'
  }
}

/**
 * rock [0|1]
 */
function parseRock(tail: string): ParsedPyMolCommand {
  const arg = tail.trim()
  
  if (arg === '0' || arg === 'off') {
    return {
      messages: [{
        description: 'SetAutoOrbit',
        result: false
      }],
      assistantResponse: 'Stopping auto-rotation'
    }
  }
  
  return {
    messages: [{
      description: 'SetAutoOrbit',
      result: { period: 10 }
    }],
    assistantResponse: 'Starting auto-rotation'
  }
}

/**
 * help [pymol]
 */
function parseHelp(tail: string): ParsedPyMolCommand {
  const helpText = `
PyMOL Lite Commands - Quick Reference

This is a simplified PyMOL command interface for molecular visualization.
For complete documentation, click the info icon (ⓘ) next to the PyMOL toggle.

Common Commands:
  fetch <pdb_id>              Load a structure from PDB
  show <style>                Display molecule (cartoon, sticks, surface, etc.)
  hide <style>                Hide representation
  color <color>               Change molecular colors
  select <selection>          Select atoms, residues, or chains
  zoom [selection]            Focus camera on selection
  rock                        Start/stop auto-rotation
  set <property>, <value>     Adjust properties (stick_radius, transparency, sweep_speed, etc.)
  bg_color <color>            Change background color

Selection Syntax:
  chain A                     Select chain A
  resi 10                     Select residue 10
  resi 10-20                  Select residues 10 through 20
  atom 42                     Select atom by ID

Examples:
  fetch 1ubq                  Load ubiquitin structure
  show surface                Display molecular surface
  color marine                Color structure marine blue
  set transparency, 0.5       Make surface semi-transparent
  select chain A              Select chain A
  zoom chain A                Zoom to chain A
  set sweep_speed, 15         Set rotation to 15 sec/rotation

For detailed help, click the ⓘ icon next to "PyMOL Lite" toggle.
`.trim()

  return {
    messages: [],
    assistantResponse: helpText
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse selection string and return FocusOnTarget parameters
 */
function parseSelection(selectionStr: string): { focus?: any; error?: string } {
  const sel = selectionStr.toLowerCase().trim()
  
  if (sel.startsWith('chain ')) {
    const chainId = selectionStr.substring(6).trim().toUpperCase()
    return { focus: { chain: chainId } }
  }
  
  if (sel.startsWith('resi ') || sel.startsWith('residue ')) {
    const resiStr = selectionStr.substring(sel.indexOf(' ') + 1).trim()
    const resiNum = parseInt(resiStr)
    if (Number.isNaN(resiNum)) {
      return { error: 'residue number must be an integer' }
    }
    return { focus: { residueId: resiNum } }
  }
  
  if (sel.startsWith('atom ')) {
    const atomStr = selectionStr.substring(5).trim()
    const atomNum = parseInt(atomStr)
    if (Number.isNaN(atomNum)) {
      return { error: 'atom number must be an integer' }
    }
    return { focus: { atomId: atomNum } }
  }
  
  return { error: `Unknown selection: ${selectionStr}` }
}
